import type { SideNav } from '@/types/sideNav'

const SIDE_NAV: SideNav = {
    account: [
        {
            label: `General Settings`,
            icon: `home`,
            route: `/account/account_settings`,
            roles: [`account_settings`]
        },
        {
            label: `Bills & Payment`,
            icon: `bill`,
            route: `/r/account/bills-and-payment`,
            roles: [`account_payment_settings`]
        },
        {
            label: `Log`,
            icon: `list`,
            route: `/account/log`,
            roles: [`account_log`]
        },
        {
            label: `Api Key`,
            icon: `code`,
            route: `/account/api`,
            roles: [`extensions_api`]
        },
        {
            label: `Restore`,
            icon: `history`,
            route: `/account/restore`,
            roles: [`account_restore`]
        },
        // Backups and factory reset should be in helm
        {
            label: `Export`,
            icon: `download`,
            route: `/account/export`,
            roles: [`account_export`]
        },
        {
            label: `Cancel Account`,
            icon: `cancel`,
            route: `/account/cancel_account`,
            roles: [`account_settings`]
        },
        {
            label: `Tithe.ly`,
            icon: `tithely`,
            route: `/account/tithely`,
            roles: [`account`, `contributions_online_giving`, `users`, `marketplace`],
            features: [`tithely-processing`]
        }
    ],
    admin: [
        {
            label: `Users`,
            icon: `user`,
            route: `/admin/users`,
            roles: [`account_settings`],
            features: [`!vue-user-mapping`]
        },
        {
            label: `Profile Mapping`,
            icon: `user`,
            route: `/admin/profile_mapping`,
            roles: [`account_settings`],
            features: [`vue-user-mapping`]
        },
        {
            label: `Roles`,
            icon: `userRoles`,
            route: `/roles/all`,
            roles: [`account_settings`]
        },
        {
            label: `Members`,
            icon: `people`,
            route: `/admin/members`,
            roles: [`account_settings`],
            features: [`!vue-user-mapping`]
        }
    ],
    events: [
        {
            label: `Overview`,
            icon: `chart-bar`,
            route: `/events/view/{instanceId}`,
            roles: [`events_report`]
        },
        {
            label: `Attendees`,
            icon: `user`,
            route: `/events/people/{instanceId}`,
            roles: [`events_report`]
        },
        {
            label: `Plan`,
            icon: `plan`,
            route: `/events/{instanceId}/plan`,
            roles: [`events_view`],
            features: [`service-planning`]
        },
        {
            label: `Reports`,
            icon: `report`,
            route: `/events/reports/{instanceId}`,
            roles: [`events_report`]
        },
        {
            label: `Attachments`,
            icon: `attachments`,
            route: `/events/{instanceId}/attachments`,
            roles: [`events_view_later`],
            features: [`service-planning`]
        },
        {
            label: `Check-in`,
            icon: `check`,
            route: `/checkin/v3/{instanceId}`,
            roles: [`checkin_general`]
        },
        {
            label: `Alerts`,
            icon: `notification`,
            route: `/events/alerts/{instanceId}`,
            roles: [`events_report`]
        },
        {
            label: `Volunteers`,
            icon: `volunteers`,
            route: `/events/volunteers2/{instanceId}`,
            roles: [`events_volunteers`]
        },
        {
            label: `Settings`,
            icon: `settings`,
            route: `/events/settings/{instanceId}`,
            roles: [`events_modify`]
        }
    ],
    people: [
        {
            label: `People`,
            icon: `people`,
            route: `/people`,
            roles: [`people`]
        },
        {
            label: `Saved Filters`,
            icon: `filters`,
            route: `/people/saved`,
            roles: [`people`],
            isPaid: true
        },
        {
            label: `Archives`,
            icon: `archives`,
            route: `/people/archives`,
            roles: [`people`]
        }
    ],
    person: [
        {
            label: `Details`,
            icon: `user`,
            route: `/people/view/{personId}`
        },
        {
            label: `Messages`,
            icon: `message`,
            route: `/people/view/{personId}/message`,
            features: [`vue-2way-messaging`],
            count: 0
        },
        {
            label: `Groups`,
            icon: `users`,
            route: `/people/view/{personId}/groups`,
            roles: [`groups_edit`],
            hasTithelyCustomerId: true,
            isPaid: true,
            count: 0
        },
        {
            label: `Tags`,
            icon: `tags`,
            route: `/people/{personId}/tags`,
            roles: [`tags`],
            isPaid: true,
            count: 0
        },
        {
            label: `Attendance`,
            icon: `calendar-blank`,
            route: `/people/view/{personId}/attendance`,
            roles: [`events_report`],
            isPaid: true,
            count: 0
        },
        {
            label: `Giving`,
            icon: `hand-coins`,
            route: `/people/view/{personId}/payments`,
            roles: [`contributions_people_reports`, `contributions_pledges`],
            isPaid: true,
            count: 0
        },
        {
            label: `Notes`,
            icon: `notepad`,
            route: `/people/{personId}/notes`,
            roles: [`person_read_notes`],
            count: 0
        },
        {
            label: `Follow-ups`,
            icon: `message`,
            route: `/people/view/{personId}/followups`,
            roles: [`followups`],
            isPaid: true,
            count: 0
        },
        {
            label: `Forms`,
            icon: `check-square`,
            route: `/people/view/{personId}/forms`,
            roles: [`forms`],
            isPaid: true,
            count: 0
        },
        {
            label: `Volunteering`,
            icon: `volunteers`,
            route: `/people/view/{personId}/volunteer`,
            roles: [`events_volunteers`],
            isPaid: true,
            count: 0
        }
    ],
    history: [
        {
            label: `People`,
            icon: `user`,
            route: `/history/people`
        },
        {
            label: `Tags`,
            icon: `tags`,
            route: `/history/tags`
        },
        {
            label: `Events`,
            icon: `events`,
            route: `/history/events`
        },
        {
            label: `Email`,
            icon: `email`,
            route: `/history/email`
        },
        {
            label: `Text`,
            icon: `text`,
            route: `/history/text`
        }
    ],
    bulk: [
        {
            label: 'Merge People',
            icon: 'merge',
            route: `/bulk/merge_people`,
            roles: [`bulk`]
        },
        {
            label: 'Update People',
            icon: 'history',
            route: `/bulk/update_people`,
            roles: [`bulk`]
        },
        {
            label: 'Import People',
            icon: 'update',
            route: `/bulk/import_people`,
            roles: [`bulk`]
        },
        {
            label: 'Import Tags',
            icon: 'tags',
            route: `/bulk/import_tags`,
            roles: [`bulk`]
        },
        {
            label: 'Import Pledges',
            icon: 'gift',
            route: `/bulk/import_pledges`,
            roles: [`bulk`],
            isTithelyGivingEnabled: false
        },
        {
            label: 'Import Giving',
            icon: 'hand-coins',
            route: `/bulk/import_giving/generic`,
            roles: [`bulk`],
            isTithelyGivingEnabled: false
        },
        {
            label: 'Import Attendance',
            icon: 'checkmark',
            route: `/bulk/import_attendance`,
            roles: [`bulk`]
        },
        {
            label: 'Import Users',
            icon: 'user',
            route: `/bulk/import_users`,
            roles: [`bulk`]
        },
        {
            label: 'Import Notes',
            icon: 'notes',
            route: `/bulk/import_notes`,
            roles: [`bulk`]
        },
        {
            label: 'Import Barcodes',
            icon: 'scan',
            route: `/bulk/import_barcodes`,
            roles: [`bulk`]
        },
        {
            label: 'Delete Data',
            icon: 'trash',
            route: `/bulk/delete`,
            roles: [`bulk`]
        }
    ]
}

export default SIDE_NAV
